import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon, 
  CameraIcon, 
  VideoCameraIcon, 
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import { Shoot, Client } from '../types'
import RevenueAnalytics from './RevenueAnalytics'
import { getExpectedShootStatus } from '../utils/dateUtils'

interface StatsModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'clients' | 'shoots' | 'upcoming' | 'revenue'
  data: {
    clients?: Client[]
    shoots?: Shoot[]
    upcomingShots?: Shoot[]
  }
  onEditShoot?: (shoot: Shoot) => void
  onDeleteShoot?: (shootId: string) => void
  onViewDetails?: (shoot: Shoot) => void
  revenueRefreshKey?: number
}

const StatsModal = ({ isOpen, onClose, type, data, onEditShoot, onDeleteShoot, onViewDetails, revenueRefreshKey }: StatsModalProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Simple overflow hidden approach that won't interfere with theme
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  // Refresh revenue analytics when modal opens for revenue type or when external refresh is triggered
  useEffect(() => {
    if (isOpen && type === 'revenue') {
      console.log('StatsModal: Triggering revenue refresh, revenueRefreshKey:', revenueRefreshKey)
      setRefreshTrigger(prev => prev + 1)
    }
  }, [isOpen, type, revenueRefreshKey])

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric' 
    })
  }

  const handleDeleteClick = (shootId: string) => {
    setDeleteConfirm(shootId)
    // Auto-cancel confirmation after 5 seconds
    setTimeout(() => setDeleteConfirm(null), 5000)
  }

  const handleConfirmDelete = (shootId: string) => {
    onDeleteShoot?.(shootId)
    setDeleteConfirm(null)
  }

  const handleCancelDelete = () => {
    setDeleteConfirm(null)
  }

  const getTitle = () => {
    switch (type) {
      case 'clients': return 'All Clients'
      case 'shoots': return 'Completed Shoots'
      case 'upcoming': return 'Scheduled Shoots'
      case 'revenue': return 'Revenue Analytics'
      default: return 'Statistics'
    }
  }

  const getIcon = () => {
    switch (type) {
      case 'clients': return UserGroupIcon
      case 'shoots': return CameraIcon
      case 'upcoming': return CalendarIcon
      case 'revenue': return Cog6ToothIcon
      default: return UserGroupIcon
    }
  }

  const Icon = getIcon()

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-4xl max-h-[90vh] sm:max-h-[80vh] glass-mockup rounded-2xl overflow-hidden mx-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">{getTitle()}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[70vh] sm:max-h-[60vh]">
              {type === 'clients' && data.clients && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.clients.map((client) => (
                    <motion.div
                      key={client.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass-mockup rounded-xl p-4"
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {client.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{client.name}</h3>
                          <p className="text-gray-400 text-sm">{client.email}</p>
                        </div>
                      </div>
                      {client.phone && (
                        <p className="text-gray-300 text-sm mb-1">📞 {client.phone}</p>
                      )}
                      {client.company && (
                        <p className="text-gray-300 text-sm">🏢 {client.company}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}

              {(type === 'shoots' || type === 'upcoming') && data.shoots && (
                <div className="space-y-4">
                  {data.shoots.map((shoot) => (
                    <motion.div
                      key={shoot.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass-mockup rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            {shoot.type === 'photo' ? (
                              <CameraIcon className="w-5 h-5 text-blue-400" />
                            ) : (
                              <VideoCameraIcon className="w-5 h-5 text-purple-400" />
                            )}
                            <h3 className="text-white font-semibold">{shoot.title}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getExpectedShootStatus(shoot.date, shoot.status) === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                              getExpectedShootStatus(shoot.date, shoot.status) === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {getExpectedShootStatus(shoot.date, shoot.status)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center space-x-2 text-gray-300">
                              <UserGroupIcon className="w-4 h-4" />
                              <span>{shoot.client?.name || 'No client'}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-300">
                              <CalendarIcon className="w-4 h-4" />
                              <span>{formatDate(shoot.date)}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-300">
                              <ClockIcon className="w-4 h-4" />
                              <span>{formatTime(shoot.start_time)} - {formatTime(shoot.end_time)}</span>
                            </div>
                            {shoot.location && (
                              <div className="flex items-center space-x-2 text-gray-300">
                                <MapPinIcon className="w-4 h-4" />
                                <span>{shoot.location}</span>
                              </div>
                            )}
                          </div>
                          
                          {shoot.notes && (
                            <div className="mt-3 p-3 bg-white/5 rounded-lg">
                              <p className="text-gray-300 text-sm">{shoot.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center space-x-2 ml-4">
                          {/* Details button */}
                          <button
                            onClick={() => onViewDetails?.(shoot)}
                            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="View details"
                          >
                            <Cog6ToothIcon className="w-4 h-4" />
                          </button>

                          {/* Edit and Delete buttons for upcoming shoots */}
                          {type === 'upcoming' && (
                            <>
                              <button
                                onClick={() => onEditShoot?.(shoot)}
                                className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                                title="Edit shoot"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              
                              {/* Delete button with slide-out confirmation */}
                              <div className="relative">
                                {deleteConfirm === shoot.id ? (
                                  <motion.div
                                    initial={{ width: 0, opacity: 0 }}
                                    animate={{ width: 140, opacity: 1 }}
                                    exit={{ width: 0, opacity: 0 }}
                                    transition={{ duration: 0.2, ease: "easeOut" }}
                                    className="flex items-center space-x-1 bg-red-500/20 rounded-lg p-1 overflow-hidden"
                                  >
                                    <button
                                      onClick={() => handleConfirmDelete(shoot.id)}
                                      className="px-2 py-1 text-xs bg-red-500 text-white hover:bg-red-600 transition-colors rounded font-medium whitespace-nowrap"
                                      title="Confirm deletion"
                                    >
                                      Yes, Delete
                                    </button>
                                    <button
                                      onClick={handleCancelDelete}
                                      className="px-2 py-1 text-xs text-gray-300 hover:text-white transition-colors rounded whitespace-nowrap"
                                      title="Cancel deletion"
                                    >
                                      Cancel
                                    </button>
                                  </motion.div>
                                ) : (
                                  <button
                                    onClick={() => handleDeleteClick(shoot.id)}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete shoot"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {type === 'revenue' && (
                <RevenueAnalytics refreshTrigger={refreshTrigger} />
              )}

              {/* Empty states */}
              {((type === 'clients' && (!data.clients || data.clients.length === 0)) ||
                ((type === 'shoots' || type === 'upcoming') && (!data.shoots || data.shoots.length === 0))) && (
                <div className="text-center py-8">
                  <Icon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {type === 'clients' ? 'No clients yet' : 
                     type === 'shoots' ? 'No completed shoots yet' : 
                     'No scheduled shoots yet'}
                  </h3>
                  <p className="text-gray-400">
                    {type === 'clients' 
                      ? 'Start by adding your first client to begin managing your business.'
                      : type === 'shoots'
                      ? 'Completed shoots will appear here after their scheduled date has passed.'
                      : 'Schedule your first shoot to get started with project management.'
                    }
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default StatsModal 